// frontend_react/src/pages/cart/CartPage.jsx

import React, { useState, useMemo, useEffect } from 'react';
import { verifyPassword } from "../../services/authApi";
import { listActivities, getActivityProducts } from "../../services/activityApi";
import { getCurrentCashSession, openGlobalCashSession, closeGlobalCashSession } from "../../services/cashRegisterApi";
import CashSessionBanner from "../../components/cash/CashSessionBanner.jsx";

import PayModal from '../../components/paymodal/index.jsx';
import CreateActivityPanel from "../../components/cart/CreateActivityPanel.jsx";
import Restaurant from '../restaurant/Restaurant.jsx';

import { calculate_unit_ht_from_ttc } from '../../utils/tax_utils';
import { useCart }     from '../../context/CartContext';
import { useAdminScope } from "../../context/AdminScopeContext";

import '../../styles/cart.css';
import cartIcon   from '../../assets/cart.svg';
import searchIcon from '../../assets/search.svg';
import creditCard from '../../assets/credit-card.svg';

import PasswordConfirmationModal from "../../components/common/PasswordConfirmationModal.jsx";

const VAT_RATE = 20;

function CartPage() {

    const [showCreateCashPasswordModal, setShowCreateCashPasswordModal] = useState(false);
    const [createCashPasswordError, setCreateCashPasswordError] = useState("");
    const [createCashPasswordLoading, setCreateCashPasswordLoading] = useState(false);

    /* ── Contextes ────────────────────────────────────────────────────────── */

    const {
        items,
        totals: { total_ttc },
        addItem,
        decrementItem,
        removeItem,
        checkout,
        confirmPayment,
        setCurrentPOSMode,
        currentPOSMode,
        currentActivityUuid,
        currentActivityName,
        loading: cartLoading,
    } = useCart();

    const [showCreateActivityPanel, setShowCreateActivityPanel] = useState(false);
    const [activities, setActivities] = useState([]);
    const [activityProducts, setActivityProducts] = useState([]);
    const [activityProductsLoading, setActivityProductsLoading] = useState(false);
    const [cashSession, setCashSession] = useState(null);
    const [cashLoading, setCashLoading] = useState(true);
    const [cashError, setCashError] = useState(null);
    const [openingCash, setOpeningCash] = useState("0");
    const [openingLoading, setOpeningLoading] = useState(false);
    const { selectedCompanyUuid } = useAdminScope();

    const [showCloseCashPanel, setShowCloseCashPanel] = useState(false);
    const [showCashSessionModal, setShowCashSessionModal] = useState(false);
    const [countedCash, setCountedCash] = useState("0");
    const [bankDeposit, setBankDeposit] = useState("0");
    const [closureComment, setClosureComment] = useState("RAS");
    const [closingLoading, setClosingLoading] = useState(false);

    /* ── États locaux ─────────────────────────────────────────────────────── */

    const [showPayModal,  setShowPayModal]  = useState(false);
    const [receiptUuid,   setReceiptUuid]   = useState(null);
    const [checkoutError, setCheckoutError] = useState(null);
    const [searchTerm,    setSearchTerm]    = useState('');

    const total_ht = calculate_unit_ht_from_ttc(total_ttc ?? 0, VAT_RATE);

    const handleCreateCashConfirmation = async (password) => {
    try {
        setCreateCashPasswordLoading(true);
        setCreateCashPasswordError("");

        await verifyPassword(password);

        setShowCreateCashPasswordModal(false);
        setShowCreateActivityPanel(true);

    } catch (error) {
        setCreateCashPasswordError(
        error?.response?.data?.error ||
        "Mot de passe incorrect."
        );
    } finally {
        setCreateCashPasswordLoading(false);
    }
    };

    const refreshCashSession = async () => {
        setCashLoading(true);
        setCashError(null);

        try {
            const data = await getCurrentCashSession({
                companyUuid: selectedCompanyUuid,
                });
            setCashSession(data);
        } catch (err) {
            console.error("Erreur chargement session caisse :", err);
            setCashError("Impossible de récupérer l'état de la caisse.");
        } finally {
            setCashLoading(false);
        }
    };

    useEffect(() => {
        refreshCashSession();
    },   [selectedCompanyUuid]);

    const handleOpenCashSession = async () => {
        setOpeningLoading(true);
        setCashError(null);

        try {
            await openGlobalCashSession({
                    opening_cash: Number(openingCash || 0),
                },
                {
                    companyUuid: selectedCompanyUuid,
                }
            );

            await refreshCashSession();
        } catch (err) {
            console.error("Erreur ouverture caisse :", err);
            setCashError("Impossible d'ouvrir la caisse.");
        } finally {
            setOpeningLoading(false);
        }
    };

    const handleCloseCashSession = async () => {
        if (!cashSession?.cash_session_uuid) {
            setCashError("Aucune session de caisse ouverte à clôturer.");
            return;
        }

        setClosingLoading(true);
        setCashError(null);

        try {
            await closeGlobalCashSession(cashSession.cash_session_uuid, {
                counted_cash: Number(countedCash || 0),
                bank_deposit: Number(bankDeposit || 0),
                closure_comment: closureComment,
              },
              {
                companyUuid: selectedCompanyUuid,
              }
            );

            setShowCloseCashPanel(false);
            await refreshCashSession();
        } catch (err) {
            console.error("Erreur clôture caisse :", err);
            setCashError(
                err?.response?.data?.error ||
                "Impossible de clôturer la caisse."
            );
        } finally {
            setClosingLoading(false);
        }
    };

    // Convertit les articles du CartContext en format attendu par PayModal.
    // Calculé une seule fois au moment de l'ouverture (snapshot).
    const payModalItems = useMemo(() => {
        if (!showPayModal) return null;
        return items.map(item => ({
            id:        item.uuid,
            name:      item.designation,
            price:     Number(item.selling_price_ttc ?? item.selling_price ?? 0),
            qty:       Number(item.quantity ?? 1),
            discounts: [],
            _uuid:     item.uuid, // conservé pour les appels API remise par article
        })).filter(i => i.qty > 0 && i.price >= 0);
    }, [showPayModal]); // eslint-disable-line react-hooks/exhaustive-deps
    // Intentionnellement pas de [items] dans les deps : on veut un snapshot
    // figé au moment de l'ouverture, pas un panier qui change en cours de paiement.

    /* ── Handlers catalogue ───────────────────────────────────────────────── */

    const visibleProducts = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();

        if (!term) return activityProducts;

        return activityProducts.filter((product) =>
            String(product.designation || "").toLowerCase().includes(term) ||
            String(product.sku || "").toLowerCase().includes(term) ||
            String(product.barcode || "").toLowerCase().includes(term)
        );
    }, [activityProducts, searchTerm]);

    const handleSearch = (e) => {
        e.preventDefault();
    };

    const handleTabChange = (mode) => {
        setCurrentPOSMode(mode);
        setSearchTerm('');
    };

    /* ── Ouverture du paiement ────────────────────────────────────────────── */

    const handleOpenPayment = async () => {
        setCheckoutError(null);
        setReceiptUuid(null);
        setShowPayModal(true); // ouvre immédiatement — les items viennent du panier

        try {
            const res = await checkout({ payments: [] });

            const uuid =
                (typeof res === 'string' ? res : null) ||
                res?.receipt_uuid  ||
                res?.receiptUuid   ||
                res?.uuid          ||
                res?.receipt?.uuid ||
                res?.data?.receipt_uuid ||
                res?.data?.uuid         ||
                res?.data?.receipt?.uuid;

            if (!uuid || typeof uuid !== 'string') {
                console.error('Checkout : uuid introuvable. res =', res);
                setCheckoutError('Ticket introuvable — les paiements ne seront pas enregistrés.');
                return;
            }

            setReceiptUuid(uuid);

        } catch (err) {
            console.error('Erreur checkout :', err);
            setCheckoutError(err?.message ?? "Erreur lors de l'ouverture du ticket.");
        }
    };

    /* ── Fermeture / validation ───────────────────────────────────────────── */

    const handleClosePayment = () => {
        setShowPayModal(false);
        setReceiptUuid(null);
        setCheckoutError(null);
    };

    const handlePaid = () => {
        confirmPayment();
        setShowPayModal(false);
        setReceiptUuid(null);
    };

    useEffect(() => {
        listActivities({
            companyUuid: selectedCompanyUuid,
            })
            .then((res) => {
                setActivities(res.data.activities ?? []);
            })
            .catch((err) => {
                console.error("Erreur chargement activités :", err);
            });
    }, [selectedCompanyUuid]);

    useEffect(() => {
        if (!currentActivityUuid) {
            setActivityProducts([]);
            return;
        }

        setActivityProductsLoading(true);

        getActivityProducts(currentActivityUuid, {
            companyUuid: selectedCompanyUuid,
            })
            .then((res) => {
            setActivityProducts(res.data.products ?? []);
            })
            .catch((err) => {
            console.error("Erreur chargement produits activité :", err);
            setActivityProducts([]);
            })
            .finally(() => {
            setActivityProductsLoading(false);
            });
    }, [currentActivityUuid, selectedCompanyUuid]);

    const isDynamicActivity =
        currentActivityUuid &&
        currentPOSMode !== "restaurant";

    /* ── Rendu ────────────────────────────────────────────────────────────── */

    if (cashLoading) {
        return (
            <div className="cash_gate_container">
                <div className="cash_gate_card card">
                    <h2>Chargement de la caisse...</h2>
                </div>
            </div>
        );
    }

    if (!cashSession?.is_open) {
        return (
            <div className="cash_gate_container">
                <div className="cash_gate_card card">
                    <h1>Caisse fermée</h1>

                    <p>
                        Aucune session de caisse n’est ouverte pour cette société.
                    </p>

                    {cashError && (
                        <p className="cash_gate_error">{cashError}</p>
                    )}

                    <label>Fond de caisse initial</label>

                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={openingCash}
                        onChange={(e) => setOpeningCash(e.target.value)}
                        placeholder="0.00"
                    />

                    <button
                        className="pay_button"
                        onClick={handleOpenCashSession}
                        disabled={openingLoading}
                    >
                        {openingLoading ? "Ouverture..." : "Ouvrir la caisse"}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', width: '100%' }}>

            <main className={`main_cart_container ${currentPOSMode === 'restaurant' ? 'restaurant_full_mode' : ''}`}>

                <div className="articles_content">
                    <div className="articles_header compact_tabs">

                        <button
                            type="button"
                            className="cash_status_button"
                            onClick={() => setShowCashSessionModal(true)}
                        >
                            <span className="cash_status_dot" />
                        </button>

                    {activities.map((activity) => (
                        <div
                            key={activity.uuid}
                            className={`tab ${currentActivityUuid === activity.uuid ? 'active_tab' : ''}`}
                            onClick={() =>
                                setCurrentPOSMode(
                                    activity.activity_type,
                                    activity.uuid,
                                    activity.name
                                )
                            }
                        >
                            {activity.name}
                        </div>
                    ))}

                        <div
                            className={`tab ${currentPOSMode === 'restaurant' ? 'active_tab' : ''}`}
                            onClick={() => handleTabChange('restaurant')}
                        >
                            Restauration
                        </div>

                        {activities.map((activity) => (
                            <div
                                key={activity.uuid}
                                className={`tab ${currentPOSMode === activity.activity_type ? 'active_tab' : ''}`}
                                onClick={() =>
                                    setCurrentPOSMode(
                                        activity.activity_type,
                                        activity.uuid,
                                        activity.name
                                    )
                                }
                            >
                                {activity.name}
                            </div>
                        ))}

                        <div
                        className="tab add_activity_tab"
                        onClick={() => {
                            setCreateCashPasswordError("");
                            setShowCreateCashPasswordModal(true);
                        }}
                        >
                        +
                        </div>
                    </div>

                    {currentPOSMode === 'restaurant' ? (
                        <div className="restaurant_embedded_container">
                            <Restaurant />
                        </div>
                    ) : (
                        <div className="articles_body">
                            <form className="store_search_bar" onSubmit={handleSearch}>
                                <button type="submit">
                                    <img src={searchIcon} className="search_icon white_svg" alt="Search" />
                                </button>
                                <input
                                    type="text"
                                    placeholder={
                                        currentActivityName
                                            ? `Rechercher dans ${currentActivityName}...`
                                            : "Sélectionnez une activité..."
                                        }
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                            </form>

                            <div className="articles_list_container">
                                {activityProductsLoading && (
                                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#fff', padding: 20 }}>
                                        Chargement...
                                    </div>
                                )}

                                {!activityProductsLoading &&
                                    !activityProductsLoading &&
                                    visibleProducts.map(product => (
                                    <div
                                        key={product.uuid}
                                        className="article_item card"
                                        onClick={() =>
                                            addItem(product.uuid, 1, {
                                                source: "activity",
                                                activityUuid: currentActivityUuid,
                                                activityType: currentPOSMode,
                                            })
                                        }
                                    >
                                        <p className="article_info">{product.designation}</p>
                                        <p className="article_price">{Number(
                                            product.selling_price_ttc ??
                                            product.selling_price ??
                                            0
                                            ).toFixed(2)}€</p>
                                    </div>
                                ))}

                                {!activityProductsLoading &&
                                    !activityProductsLoading &&
                                    visibleProducts.length === 0 && (
                                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 20, opacity: 0.5 }}>
                                        Aucun résultat.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {currentPOSMode !== 'restaurant' && (
                    <div className="order_section">
                        <div className="text_with_icon">
                            <h2>COMMANDE</h2>
                            <img src={cartIcon} className="cart_icon white_svg" alt="Cart" />
                        </div>

                        <div className="order_summary card">
                            {items.length === 0 && (
                                <div style={{ textAlign: 'center', padding: 20, opacity: 0.5 }}>
                                    Panier vide
                                </div>
                            )}

                            {items.map(item => (
                                <div className="cart_item" key={item.uuid}>
                                    <div className="quantity_row">
                                        <div className="controls">
                                            <button
                                                className="decrease_quantity"
                                                onClick={() => item.quantity > 1
                                                    ? decrementItem(item.uuid)
                                                    : removeItem(item.uuid)}
                                            >−</button>

                                            <span className="item_quantity">{item.quantity}</span>

                                            <button
                                                className="increase_quantity"
                                                onClick={() => addItem(item.uuid, 1)}
                                            >+</button>
                                        </div>
                                    </div>

                                    <div className="item_details">
                                        <p className="item_name">{item.designation}</p>
                                    </div>

                                    <div className="item_price">
                                        <p>{(item.quantity * (item.selling_price_ttc ?? 0)).toFixed(2)}€</p>
                                    </div>
                                </div>
                            ))}

                            <div className="cart_purchase_info">
                                <div className="info_row muted_text">
                                    <p>HT</p>
                                    <p>{total_ht.toFixed(2)}€</p>
                                </div>

                                <div className="info_row">
                                    <p>Total TTC</p>
                                    <p>{(total_ttc ?? 0).toFixed(2)}€</p>
                                </div>
                            </div>
                        </div>

                        <div className="text_with_icon">
                            <h2>REGLEMENT</h2>
                            <img src={cartIcon} className="cart_icon white_svg" alt="Cart" />
                        </div>

                        <button
                            className="pay_button text_with_icon"
                            onClick={handleOpenPayment}
                            disabled={items.length === 0 || cartLoading}
                            style={{
                                opacity: items.length === 0 || cartLoading ? 0.6 : 1,
                                cursor: items.length === 0 || cartLoading ? 'not-allowed' : 'pointer',
                                justifyContent: 'center',
                            }}
                        >
                            <img src={creditCard} alt="Pay" />
                            <p>{cartLoading ? '...' : 'Régler'}</p>
                        </button>
                    </div>
                )}
            </main>

            {showCreateActivityPanel && (
                <CreateActivityPanel
                    onClose={() => setShowCreateActivityPanel(false)}
                    onCreated={(activity) => {
                        setShowCreateActivityPanel(false);

                        setActivities((prev) => [
                            ...prev.filter((item) => item.uuid !== activity.uuid),
                            activity,
                        ]);

                        setCurrentPOSMode(
                            activity.activity_type,
                            activity.uuid,
                            activity.name
                        );
                    }}
                />
            )}

            <PasswordConfirmationModal
                open={showCreateCashPasswordModal}
                badge="Administration"
                title="Création d'une nouvelle caisse"
                message="La création d'une nouvelle caisse ajoute un nouvel environnement de vente dans Queen's 360. Veuillez confirmer votre identité avec votre mot de passe pour poursuivre."
                confirmLabel="Créer une caisse"
                loading={createCashPasswordLoading}
                error={createCashPasswordError}
                onConfirm={handleCreateCashConfirmation}
                onCancel={() => {
                    setShowCreateCashPasswordModal(false);
                    setCreateCashPasswordError("");
                }}
            />

            {showPayModal && (
                <PayModal
                    receiptUuid={receiptUuid}
                    initialItems={payModalItems}
                    onClose={handleClosePayment}
                    onPaid={handlePaid}
                    pdfFormat="A4"
                    checkFormat={true}
                />
            )}

            {showCashSessionModal && (
                <CashSessionBanner
                    cashSession={cashSession}
                    onCloseClick={() => {
                        setShowCashSessionModal(false);
                        setShowCloseCashPanel(true);
                    }}
                    onClose={() => setShowCashSessionModal(false)}
                />
            )}

            {showCloseCashPanel && (
                <div className="cash_close_overlay">
                    <div className="cash_close_panel card">
                        <h2>Clôture rapide de caisse</h2>

                        {cashError && (
                            <p className="cash_gate_error">{cashError}</p>
                        )}

                        <label>Espèces constatées</label>
                        <input
                            type="number"
                            step="0.01"
                            value={countedCash}
                            onChange={(e) => setCountedCash(e.target.value)}
                        />

                        <label>Dépôt banque</label>
                        <input
                            type="number"
                            step="0.01"
                            value={bankDeposit}
                            onChange={(e) => setBankDeposit(e.target.value)}
                        />

                        <label>Commentaire</label>
                        <input
                            type="text"
                            value={closureComment}
                            onChange={(e) => setClosureComment(e.target.value)}
                        />

                        <div className="cash_close_actions">
                            <button
                                type="button"
                                className="cart_cash_action_button"
                                onClick={() => setShowCloseCashPanel(false)}
                            >
                                Annuler
                            </button>

                            <button
                                type="button"
                                className="cart_cash_close_button"
                                onClick={handleCloseCashSession}
                                disabled={closingLoading}
                            >
                                {closingLoading ? "Clôture..." : "Valider clôture Z"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default CartPage;